import type { Store } from '../state/store.svelte';
import { simSecondsForFrame } from '../state/ticker';
import { selectSpeedScale } from '../state/selectors';

/**
 * The requestAnimationFrame simulation clock (a browser concern wired by the
 * composition root, AD-1/AD-2: no `requestAnimationFrame` or `performance.now()`
 * in `core` or `state`). Each frame it measures real elapsed seconds, converts
 * them to a simulation `dt` via `simSecondsForFrame` (the single sim-speed ->
 * `dt` conversion, in `state`), and calls `store.tick(dt)`. The store replaces
 * its state object, so `CanvasHost`'s existing `$effect` re-renders; no second
 * render trigger is added, and render/UI stay read-only (AD-2).
 *
 * Play gate (Story 1.5): the loop advances the clock ONLY when playback is running
 * (`!store.state.paused`), so Pause halts motion in EVERY environment (including
 * development). The "dev auto-runs on load" convenience is preserved a different
 * way, at the composition root (`main.ts`): on cold load it dispatches `Play` in
 * development or under the `__KEPLER_AUTORUN__` test seam, flipping `paused` to
 * false so this loop then advances. No env force-tick lives here, so a real Pause
 * is never overridden.
 */

/** Clamp so a backgrounded tab (large frame gap) does not jump the simulation. */
const MAX_REAL_DELTA_SECONDS = 1 / 15;

export interface AnimationLoop {
  /** Stop the loop and release the frame request. */
  stop(): void;
}

export function startAnimationLoop(
  store: Store,
  options: {
    /** Injectable clock for testing; defaults to `performance.now` in ms. */
    now?: () => number;
    /** Injectable RAF scheduler; defaults to `requestAnimationFrame`. */
    requestFrame?: (cb: FrameRequestCallback) => number;
    cancelFrame?: (handle: number) => void;
  } = {}
): AnimationLoop {
  const now = options.now ?? (() => performance.now());
  const requestFrame =
    options.requestFrame ?? ((cb: FrameRequestCallback) => requestAnimationFrame(cb));
  const cancelFrame = options.cancelFrame ?? ((h: number) => cancelAnimationFrame(h));

  let handle = 0;
  let last = now();
  let running = true;

  const frame = (): void => {
    if (!running) return;
    const current = now();
    const realElapsed = Math.min((current - last) / 1000, MAX_REAL_DELTA_SECONDS);
    last = current;
    // Advance only when playback is running; paused halts motion everywhere.
    // (`advanceTime` also guards on `paused`, so this is the primary gate.)
    if (!store.state.paused) {
      store.tick(
        simSecondsForFrame(
          realElapsed,
          store.state.speedDaysPerSecond,
          selectSpeedScale(store.state)
        )
      );
    }
    handle = requestFrame(frame);
  };

  handle = requestFrame(frame);

  return {
    stop() {
      running = false;
      cancelFrame(handle);
    }
  };
}
